/**
 * 「仕入登録」/purchases/new（#233 3a、docs/93 §15.4 の入口3＝{order}なし）：{vendor}と{project}（任意）だけ聞く。?group=・?partner= で始められる。
 * {order}から（⋯「仕入登録」）と受け取った書類から（AI読み取り結果の「仕入登録」）はここを通らない。PC だけ（PC_ONLY_FEATURES）
 */
import { PURCHASE_MSG, type GroupList, type Partner, type Purchase } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { ErrorText, Field } from "../../components/ui.jsx";
import { PartnerField } from "../../components/PartnerPickerDrawer.jsx";
import { t, useLabels } from "../../labels.js";

export function PurchaseNewPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [vendorId, setVendorId] = useState(sp.get("partner") ?? "");
  const [groupId, setGroupId] = useState(sp.get("group") ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  usePageName(PURCHASE_MSG.newTitle);
  const back = useBackNav(groupId && sp.get("group") ? { to: `/groups/${groupId}?tab=purchases`, label: L.project } : { to: "/purchases", label: PURCHASE_MSG.list }, !!vendorId);
  /** 選んだ{vendor}（#289：取引先検索のドロアー＝#288 の部品。?partner= で入ったときは名前を引く） */
  const [picked, setPicked] = useState<{ name: string; branch: string | null; contact: string | null } | null>(null);
  const entry = useQuery({ queryKey: ["partner", sp.get("partner")], queryFn: () => api<Partner>("GET", `/partners/${sp.get("partner")}`), enabled: !!sp.get("partner") });
  const shown = picked ?? (entry.data && entry.data.id === vendorId ? { name: entry.data.displayName, branch: null, contact: null } : null);
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all") });
  const create = async () => {
    if (!vendorId) {
      setErr(t(PURCHASE_MSG.vendorRequired));
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const p = await api<Purchase>("POST", "/purchases", { vendorId, projectGroupId: groupId || null });
      void qc.invalidateQueries({ queryKey: ["purchases"] });
      nav(`/purchases/${p.id}`, { replace: true });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{PURCHASE_MSG.newTitle}</h1>
      </div>
      <div className="paper" style={{ maxWidth: 640, padding: 20 }} data-testid="purchase-new-form">
        <div style={{ marginBottom: 14 }}>
          <PartnerField
            role="vendor"
            label={L.vendor}
            value={shown}
            onPick={(c) => {
              setVendorId(c.id);
              setPicked({ name: c.displayName, branch: null, contact: null });
              setErr(undefined);
            }}
            testId="purchase-new-vendor"
          />
        </div>
        <Field id="pn_group" label={`${L.project}（任意）`} style={{ marginBottom: 14 }}>
          <select id="pn_group" className="inp" value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="purchase-new-group">
            <option value="">—</option>
            {(groups.data?.items ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </Field>
        <ErrorText>{err}</ErrorText>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn primary" disabled={busy || !vendorId} onClick={() => void create()} data-testid="purchase-new-create">
            {PURCHASE_MSG.create}
          </button>
        </div>
      </div>
    </>
  );
}
